import axios from "axios";
import Cookies from "js-cookie";
import { extractKpiApiError } from "./kpiPlatformApi";
import {
  getActiveOfficeCodeFromStorage,
  resolveAssignableOrganizations,
} from "../../../../../utils/officeCodeContext";

const API_ROOT = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const authHeaders = () => {
  const token = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCode") || getActiveOfficeCodeFromStorage();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(officeCode ? { "Office-Code": officeCode } : {}),
  };
};

function resolveTenantOfficeCode(explicitOffice) {
  const code = String(explicitOffice || getActiveOfficeCodeFromStorage() || "").trim();
  if (!code) {
    throw new Error(
      "Tenant office_code is required. Select an active organization and try again."
    );
  }
  return code;
}

function orgCarrierKpiUrl(tenantOffice) {
  const officeCode = resolveTenantOfficeCode(tenantOffice);
  return `${API_ROOT}/api/organizations/${encodeURIComponent(officeCode)}/carrier-kpi`;
}

export function normalizeCarrierKpiRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const laneKey = String(raw.lane_key ?? raw.laneKey ?? "").trim();

  return {
    id: raw.id,
    carrierCode: raw.carrier_code ?? raw.carrierCode ?? "",
    officeCode: raw.office_code ?? raw.officeCode ?? "",
    laneKey,

    kpiName: raw.kpi_name ?? raw.kpiName ?? "",
    kpiValue: raw.kpi_value ?? raw.kpiValue,
    sampleSize: raw.sample_size ?? raw.sampleSize,
    windowStart: raw.window_start ?? raw.windowStart,
    windowEnd: raw.window_end ?? raw.windowEnd,
    computedAt: raw.computed_at ?? raw.computedAt,
  };
}

/** Human label for carrier_kpi.lane_key (empty = office-wide aggregate per backend). */
export function formatCarrierKpiLaneKey(laneKey) {
  const key = String(laneKey ?? "").trim();
  if (!key) return "Office-wide";
  return key.replace(/->/g, " → ");
}


/** Offices the signed-in user can access (login cookie + organizationData). */
export function resolveEffectiveAssignedOfficeCodes(explicitCodes) {
  const fromArg = (explicitCodes || [])
    .map((code) => String(code).trim())
    .filter(Boolean);
  if (fromArg.length) return [...new Set(fromArg)];

  const fromOrgs = resolveAssignableOrganizations()
    .map((org) => String(org.officeCode || "").trim())
    .filter(Boolean);
  return [...new Set(fromOrgs)];
}

function normalizeOfficeCode(code) {
  return String(code || "").trim().toUpperCase();
}

/** Comma-separated office_codes query value for org-scoped carrier-kpi APIs. */
export function formatOfficeCodesParam(explicitCodes) {
  const codes = resolveEffectiveAssignedOfficeCodes(explicitCodes);
  if (!codes.length) {
    throw new Error(
      "At least one office_code is required. Ensure your account has assigned offices."
    );
  }
  return codes.join(",");
}

function withOfficeCodesParam(params, officeCodes) {
  const office_codes = formatOfficeCodesParam(officeCodes);
  return { ...params, office_codes };
}

/** GET /api/organizations/{officeCode}/carrier-kpi?office_codes=... */
export async function fetchCarrierKpis({
  carrierCode,
  kpiName,
  laneKey,

  officeCodes,
  tenantOffice,
} = {}) {
  const params = {};
  const carrier = String(carrierCode || "").trim();
  const kpi = String(kpiName || "").trim();
  const lane = String(laneKey ?? "").trim();
  if (carrier) params.carrier_code = carrier;
  if (kpi) params.kpi_name = kpi;
  if (lane) params.lane_key = lane;

  try {
    const { data } = await axios.get(orgCarrierKpiUrl(tenantOffice), {
      headers: authHeaders(),
      params: withOfficeCodesParam(params, officeCodes),
    });
    const list = Array.isArray(data) ? data : data?.items ?? data?.data ?? [];
    return list.map(normalizeCarrierKpiRow).filter(Boolean);
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to load carrier KPIs."));
  }
}

/** Carrier KPI names tied to a base KPI catalog entry (variant carrier_kpi_name values). */
export function collectCarrierKpiNamesForBase(base) {
  const names = new Set();
  const laneBase =
    base?.laneScoped || String(base?.kpiCode || "").startsWith("lane_level_");
  if (laneBase) {
    const code = String(base.kpiCode || "").trim();
    if (code) names.add(code);
  }

  for (const variant of base?.selectionVariants || []) {
    for (const candidate of [
      variant.carrierKpiName,
      variant.scoringWeightKey,
      variant.variantCode,
    ]) {
      const name = String(candidate || "").trim();
      if (name) names.add(name);
    }
  }
  if (!names.size && base?.kpiCode) {
    names.add(String(base.kpiCode).trim());
  }
  return [...names].sort();
}

/** Build map of carrier KPI name -> base catalog entries that use it. */
export function buildKpiNameToBaseMap(catalog) {
  const map = new Map();
  const addMapping = (rawName, base) => {
    const normalized = String(rawName || "").trim();
    if (!normalized || !base?.kpiCode) return;
    const key = normalized.toUpperCase();
    const existing = map.get(key) || [];
    if (!existing.some((entry) => entry.kpiCode === base.kpiCode)) {
      existing.push(base);
    }
    map.set(key, existing);
  };

  for (const base of catalog || []) {
    if (!base?.kpiCode) continue;
    for (const name of collectCarrierKpiNamesForBase(base)) {
      addMapping(name, base);
    }
    for (const variant of base.selectionVariants || []) {
      addMapping(variant.carrierKpiName, base);
      addMapping(variant.scoringWeightKey, base);
      addMapping(variant.variantCode, base);
    }
  }
  return map;
}

export function findCatalogBaseForCarrierKpiName(catalog, kpiName) {
  const normalized = String(kpiName || "").trim();
  if (!normalized) return null;
  const map = buildKpiNameToBaseMap(catalog);
  const fromMap = map.get(normalized.toUpperCase());
  if (fromMap?.length) return fromMap[0];

  const lower = normalized.toLowerCase();
  for (const base of catalog || []) {
    const code = String(base?.kpiCode || "").trim();
    if (!code) continue;
    if (lower === code.toLowerCase() || lower.startsWith(`${code.toLowerCase()}_`)) {
      return base;
    }
  }
  return null;
}

export function attachBaseMetadataToCarrierRows(rows, catalog, fallbackBase = null) {
  return (rows || []).map((row) => {
    const base = fallbackBase || findCatalogBaseForCarrierKpiName(catalog, row.kpiName);
    if (!base) {
      return {
        ...row,
        baseKpiCode: row.baseKpiCode || "",
        baseDisplayName: row.baseDisplayName || row.kpiName || "",
      };
    }
    return {
      ...row,
      baseKpiCode: base.kpiCode,
      baseDisplayName: base.displayName,
    };
  });
}

export function notifyCarrierKpiDataChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("carrier-kpi-data-changed"));
  }
}


function buildCarrierKpiRowKey(row, baseKpiCode = "") {
  const idPart = row.id != null ? String(row.id) : "no-id";
  return [
    baseKpiCode || "",
    idPart,
    row.carrierCode || "",
    row.officeCode || "",
    row.laneKey ?? "",

    row.kpiName || "",
    row.windowStart || "",
    row.windowEnd || "",
  ].join("|");
}

/** Attach base KPI metadata to a carrier row (one row per matching base when names overlap). */
export function enrichCarrierKpiRowsForCatalog(rows, catalog) {
  const kpiNameToBases = buildKpiNameToBaseMap(catalog);
  const kpiNameLookup = new Map();
  for (const [name, bases] of kpiNameToBases.entries()) {
    kpiNameLookup.set(name.toUpperCase(), bases);
  }
  const kpiNameSet = new Set();
  const byKey = new Map();

  for (const row of rows || []) {
    const kpiName = String(row.kpiName || "").trim();
    if (!kpiName) continue;

    kpiNameSet.add(kpiName);
    const bases = kpiNameLookup.get(kpiName.toUpperCase());

    if (bases?.length) {
      for (const base of bases) {
        const enriched = {
          ...row,
          baseKpiCode: base.kpiCode,
          baseDisplayName: base.displayName,
        };
        byKey.set(buildCarrierKpiRowKey(row, base.kpiCode), enriched);
      }
      continue;
    }

    const fallbackBase = findCatalogBaseForCarrierKpiName(catalog, kpiName);
    const enriched = fallbackBase
      ? {
          ...row,
          baseKpiCode: fallbackBase.kpiCode,
          baseDisplayName: fallbackBase.displayName,
        }
      : {
          ...row,
          baseKpiCode: "",
          baseDisplayName: kpiName,
        };
    byKey.set(buildCarrierKpiRowKey(row, fallbackBase?.kpiCode || kpiName), enriched);
  }

  const enrichedRows = attachBaseMetadataToCarrierRows([...byKey.values()], catalog).sort(
    (a, b) =>
      (a.baseDisplayName || "").localeCompare(b.baseDisplayName || "") ||
      a.carrierCode.localeCompare(b.carrierCode) ||
      (a.laneKey || "").localeCompare(b.laneKey || "") ||
      a.kpiName.localeCompare(b.kpiName) ||
      String(b.computedAt || "").localeCompare(String(a.computedAt || ""))
  );

  return {
    kpiNames: [...kpiNameSet].sort(),
    rows: enrichedRows,
  };
}

/** Keep rows whose office is in the user's assigned office list (case-insensitive). */
export function filterCarrierKpisByAssignedOffices(rows, allowedOfficeCodes) {
  const allowed = new Set(
    resolveEffectiveAssignedOfficeCodes(allowedOfficeCodes).map(normalizeOfficeCode)
  );
  if (!allowed.size) return rows || [];

  return (rows || []).filter((row) => {
    const office = normalizeOfficeCode(row.officeCode);
    return office && allowed.has(office);
  });
}

/** Load carrier_kpi rows for all carriers, merged across this base KPI's variant names. */
export async function fetchCarrierKpisForBaseKpi(base, { allowedOfficeCodes, tenantOffice } = {}) {
  const kpiNames = collectCarrierKpiNamesForBase(base);
  if (!kpiNames.length) {
    return { kpiNames, rows: [], totalBeforeOfficeFilter: 0, officesUsed: [] };
  }

  const officesUsed = resolveEffectiveAssignedOfficeCodes(allowedOfficeCodes);

  const batches = await Promise.all(
    kpiNames.map((kpiName) =>
      fetchCarrierKpis({ kpiName, officeCodes: officesUsed, tenantOffice })
    )
  );
  const byKey = new Map();
  for (const batch of batches) {
    for (const row of batch) {
      byKey.set(buildCarrierKpiRowKey(row, row.kpiName || ""), row);
    }
  }

  const allRows = [...byKey.values()];
  const totalBeforeOfficeFilter = allRows.length;
  const officeFiltered = officesUsed.length
    ? filterCarrierKpisByAssignedOffices(allRows, officesUsed)
    : allRows;
  const rows = attachBaseMetadataToCarrierRows(officeFiltered, [base], base).sort(
    (a, b) =>
      a.carrierCode.localeCompare(b.carrierCode) ||
      a.kpiName.localeCompare(b.kpiName) ||
      String(b.computedAt || "").localeCompare(String(a.computedAt || ""))
  );

  return { kpiNames, rows, totalBeforeOfficeFilter, officesUsed };
}

/** Load carrier_kpi rows for every base KPI in the catalog (merged). */
export async function fetchCarrierKpisForCatalog(catalog, { allowedOfficeCodes, tenantOffice } = {}) {
  const officesUsed = resolveEffectiveAssignedOfficeCodes(allowedOfficeCodes);

  if (!(catalog || []).length) {
    return { kpiNames: [], rows: [], totalBeforeOfficeFilter: 0, officesUsed };
  }

  const allRows = await fetchCarrierKpis({ officeCodes: officesUsed, tenantOffice });
  const totalBeforeOfficeFilter = allRows.length;
  const officeFiltered = officesUsed.length
    ? filterCarrierKpisByAssignedOffices(allRows, officesUsed)
    : allRows;
  const { kpiNames, rows } = enrichCarrierKpiRowsForCatalog(officeFiltered, catalog);

  return {
    kpiNames,
    rows,
    totalBeforeOfficeFilter,
    officesUsed,
  };
}

/**
 * POST /api/organizations/{officeCode}/carrier-kpi/sync-selection-variants?office_codes=...
 * @param {{ variantCode?: string, officeCodes?: string[], tenantOffice?: string }} options
 */
export async function syncCarrierSelectionVariants({
  variantCode,
  officeCodes,
  tenantOffice,
} = {}) {
  const params = {};
  const code = String(variantCode || "").trim();
  if (code) params.variant_code = code;

  try {
    const { data } = await axios.post(
      `${orgCarrierKpiUrl(tenantOffice)}/sync-selection-variants`,
      null,
      {
        headers: authHeaders(),
        params: withOfficeCodesParam(params, officeCodes),
      }
    );
    return data ?? {};
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to sync carrier KPIs from selection variants."));
  }
}

/**
 * POST /api/organizations/{officeCode}/carrier-kpi/sync-all?office_codes=...
 * @param {{ officeCodes?: string[], tenantOffice?: string }} options
 */
function rowsWrittenForLaneBaseKpi(payload, kpiCode) {
  if (!payload || typeof payload !== "object") return 0;
  const direct = Number(payload.rows_written);
  if (Number.isFinite(direct) && direct >= 0 && payload.kpi_code === kpiCode) {
    return direct;
  }
  const laneMap = payload.lane_base_kpis;
  if (laneMap && typeof laneMap === "object" && !Array.isArray(laneMap)) {
    const fromMap = Number(laneMap[kpiCode]);
    if (Number.isFinite(fromMap)) return fromMap;
  }
  const bases = payload.bases || payload.lane_base_kpis?.bases;
  if (Array.isArray(bases)) {
    const match = bases.find((row) => String(row?.kpi_code || "").trim() === kpiCode);
    if (match) return Number(match.rows_written) || 0;
  }
  return Number.isFinite(direct) ? direct : 0;
}

function isMissingSyncLaneBaseHandler(err) {
  const status = err?.response?.status;
  const msg = String(
    err?.response?.data?.message || err?.response?.data?.error || err?.message || ""
  ).toLowerCase();
  return status === 404 && msg.includes("sync-lane-base");
}

/**
 * POST /api/organizations/{officeCode}/carrier-kpi/sync-lane-base?kpi_code=...&office_codes=...
 * Falls back to sync-all / backfill when the server build predates sync-lane-base (404).
 * @param {{ kpiCode: string, officeCodes?: string[], tenantOffice?: string }} options
 */
export async function syncLaneBaseCarrierKpi({ kpiCode, officeCodes, tenantOffice } = {}) {
  const code = String(kpiCode || "").trim();
  if (!code) {
    throw new Error("kpi_code is required for lane base carrier KPI sync.");
  }

  const baseUrl = orgCarrierKpiUrl(tenantOffice);
  const requestConfig = {
    headers: authHeaders(),
    params: withOfficeCodesParam({ kpi_code: code }, officeCodes),
  };

  try {
    const { data } = await axios.post(`${baseUrl}/sync-lane-base`, null, requestConfig);
    return data ?? {};
  } catch (err) {
    if (!isMissingSyncLaneBaseHandler(err)) {
      throw new Error(extractKpiApiError(err, "Failed to sync lane-level carrier KPI."));
    }

    try {
      const { data } = await axios.post(`${baseUrl}/sync-all`, null, {
        headers: authHeaders(),
        params: withOfficeCodesParam({}, officeCodes),
      });
      const lanePayload = data?.lane_base_kpis ?? data;
      const rows = rowsWrittenForLaneBaseKpi(lanePayload, code);
      if (rows > 0 || (lanePayload?.bases && lanePayload.bases.some((b) => b?.kpi_code === code))) {
        return {
          kpi_code: code,
          rows_written: rowsWrittenForLaneBaseKpi(lanePayload, code),
          fallback: "sync-all",
          ...lanePayload,
        };
      }
    } catch (syncAllErr) {
      if (syncAllErr?.response?.status !== 404) {
        throw new Error(
          extractKpiApiError(syncAllErr, "Lane KPI sync-all fallback failed.")
        );
      }
    }

    try {
      const { data } = await axios.post(`${baseUrl}/backfill`, null, {
        headers: authHeaders(),
        params: withOfficeCodesParam({ lookback_days: 90 }, officeCodes),
      });
      const rows = rowsWrittenForLaneBaseKpi(data, code);
      if (rows > 0 || data?.lane_base_kpis) {
        return {
          kpi_code: code,
          rows_written: rows,
          fallback: "backfill",
        };
      }
    } catch (backfillErr) {
      throw new Error(
        extractKpiApiError(
          backfillErr,
          "Lane KPI sync is not available on this server. Deploy the backend build that includes POST .../carrier-kpi/sync-lane-base (lane-level KPI changes)."
        )
      );
    }

    throw new Error(
      "Lane KPI sync is not available on this server. Deploy the backend build that includes POST .../carrier-kpi/sync-lane-base (lane-level KPI changes)."
    );
  }
}


export async function syncAllCarrierKpis({ officeCodes, tenantOffice } = {}) {
  try {
    const { data } = await axios.post(
      `${orgCarrierKpiUrl(tenantOffice)}/sync-all`,
      null,
      {
        headers: authHeaders(),
        params: withOfficeCodesParam({}, officeCodes),
      }
    );
    return data ?? {};
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to sync all carrier KPIs."));
  }
}

/**
 * POST /api/organizations/{officeCode}/carrier-kpi/backfill?lookback_days=...&office_codes=...
 * @param {{ lookbackDays?: number, officeCodes?: string[], tenantOffice?: string }} options
 */
export async function backfillCarrierKpis({ lookbackDays, officeCodes, tenantOffice } = {}) {
  const params = {};
  if (lookbackDays != null && Number.isFinite(Number(lookbackDays))) {
    params.lookback_days = Number(lookbackDays);
  }

  try {
    const { data } = await axios.post(
      `${orgCarrierKpiUrl(tenantOffice)}/backfill`,
      null,
      {
        headers: authHeaders(),
        params: withOfficeCodesParam(params, officeCodes),
      }
    );
    return data ?? {};
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to backfill carrier KPIs."));
  }
}
